import React from 'react';

interface Service {
  title: string;
  description: string;
  tags: string[];
  icon: React.ReactNode;
}

const SERVICES: Service[] = [
  {
    title: 'Custom Software Development',
    description:
      "We build software from scratch, tailored to your exact business needs. Whether it's a customer portal, an internal tool, or a full product — we design, develop, and deliver it end-to-end.",
    tags: ['Web Apps', 'Internal Tools', 'APIs', 'TypeScript · Python'],
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
      </svg>
    ),
  },
  {
    title: 'AI-Powered Tools',
    description:
      'We integrate AI into your business workflows — from intelligent automations and smart assistants to data analysis tools — making your team faster and your decisions smarter.',
    tags: ['AI Automation', 'Claude AI', 'LLM Integration', 'Smart Workflows'],
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17H3a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v10a2 2 0 01-2 2h-2" />
      </svg>
    ),
  },
  {
    title: 'CRM & Business Systems',
    description:
      'We design custom CRM and operations systems built around how your business actually works — helping you manage clients, automate follow-ups, and reduce overhead costs significantly.',
    tags: ['Custom CRM', 'Restaurant Tech', 'Operations', 'Cost Savings'],
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    ),
  },
  {
    title: 'Cloud Architecture',
    description:
      'We design and deploy scalable, resilient cloud infrastructure on AWS, Azure, and GCP. From greenfield architectures to multi-cloud strategies, built with security and cost-efficiency at the core.',
    tags: ['AWS', 'Azure', 'GCP', 'Multi-Cloud'],
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
      </svg>
    ),
  },
  {
    title: 'DevOps & CI/CD',
    description:
      'We automate your entire software delivery lifecycle. Robust CI/CD pipelines, GitOps workflows, and Infrastructure as Code that turn manual deployments into repeatable, auditable processes.',
    tags: ['CI/CD', 'GitOps', 'Terraform', 'GitHub Actions'],
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
  },
  {
    title: 'Cloud Cost Optimization',
    description:
      'We eliminate cloud waste without sacrificing performance. Right-sizing, reserved instance strategies, FinOps dashboards, and continuous cost monitoring to reduce your bill by up to 40%.',
    tags: ['FinOps', 'Right-Sizing', 'Reserved Instances', 'Tagging'],
    icon: (
      <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
];

const Features: React.FC = () => {
  return (
    <section id="services" className="py-24 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-2xl mb-16">
          <p className="text-blue-700 text-sm font-semibold uppercase tracking-widest mb-3">
            What We Do
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">
            Software, Cloud & AI — End to End
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            From the first line of code to a live, deployed system — we handle every step so you can focus on running your business.
          </p>
        </div>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((service) => (
            <div
              key={service.title}
              className="bg-white rounded-2xl p-7 border border-gray-200 hover:border-blue-200 hover:shadow-lg transition-all duration-300 group"
            >
              <div className="w-12 h-12 bg-blue-50 group-hover:bg-blue-100 rounded-xl flex items-center justify-center text-blue-700 mb-5 transition-colors duration-200">
                {service.icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{service.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-5">{service.description}</p>
              <div className="flex flex-wrap gap-2">
                {service.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-md"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 bg-gradient-to-r from-blue-700 to-blue-800 rounded-2xl p-10 text-white text-center">
          <h3 className="text-2xl font-bold mb-3">Have a project in mind?</h3>
          <p className="text-blue-100 mb-7 max-w-xl mx-auto">
            Tell us what your business needs and we'll build the right solution — on time, on budget, and built to last.
          </p>
          <a
            href="#contact"
            className="inline-flex items-center bg-white text-blue-700 hover:bg-blue-50 font-semibold px-8 py-3 rounded-lg transition-colors duration-200"
          >
            Schedule a Free Consultation
          </a>
        </div>
      </div>
    </section>
  );
};

export default Features;
